import { Observable, Subscription, zip } from "rxjs";
import { skip } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import {
  AppState,
  getLoadingVehiclesState,
  loadedCategoriesListPartyState,
  loadedPropertiesListPartyState,
  loadedVehiclesListState,
  vehiclesStateExcelList,
  vehiclesStateTotalVehicles,
} from "src/app/store/reducers";
import {
  getCategoriesForListByUsage,
  getPropertiesForListByUsage,
} from "src/app/store/actions/parties/parties.actions";

import { VehicleDTO } from "src/app/models/Vehicles/VehicleDTO";
import { CategoryDTO } from "src/app/models/Categories/categoryDTO";
import { PropertyDTO } from "src/app/models/Properties/propertyDTO";
import {
  ColumnSorterInfo,
  ConfigScope,
  ConfigSettingType,
  ExportEvent,
  FieldType,
  FormatType,
  TableConfig,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";

import { setCategories, setProperties } from "../../utils/tableUtils";
import { CHIP_STYLE, getChipStyle } from "../../utils/customTableUtils";
import {
  getVehicleTableConfig,
  saveVehicleTableConfig,
} from "src/app/store/actions/vehicles/vehicles.actions";
import { TranslateService } from "@ngx-translate/core";

const USAGE_CODE = "VEHICLE";

export class BaseVehiclesList {
  isLoading$: Observable<boolean>;

  columnInfo$: Observable<ColumnSorterInfo[]>;

  vehicleList: VehicleDTO[];
  vehicleList$: Observable<VehicleDTO[]>;
  excelVehicleList$: Observable<VehicleDTO[]>;
  totalVehicles$: Observable<number>;

  loadedVehicletSub: Subscription;
  multiSubs: Subscription;

  categories$: Observable<CategoryDTO[]>;
  properties$: Observable<PropertyDTO[]>;

  formPath: string = "vehicles/vehicle";
  tableTitle: string;
  headerIcon: string;

  tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "Vehicles.VehicleCode",
      field: "code",
      fieldType: FieldType.STRING,
      identifier: true,
      searchable: true,
    },
    {
      header: "Vehicles.Status",
      field: "statusDescription",
      fieldType: FieldType.CHIP_STRING,
    },
    {
      header: "Vehicles.LicensePlate",
      field: "licensePlate",
      fieldType: FieldType.CHIP_STRING,
      searchable: true,
    },
    {
      header: "Vehicles.Description",
      field: "description",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Vehicles.Brand",
      field: "brandDescription",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Vehicles.Model",
      field: "modelDescription",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Vehicles.Version",
      field: "versionDescription",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Vehicles.CustomerCode",
      field: "customer.customerCode",
      fieldType: FieldType.STRING,
      hidden: true,
      searchable: true,
    },
    {
      header: "Vehicles.CustomerVATIn",
      field: "customer.vatin",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Vehicles.CustomerName",
      field: "customer.name",
      fieldType: FieldType.STRING,
    },
    {
      header: "Vehicles.TotalOdometerList",
      field: "consumptionData.totalOdometer",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Vehicles.TotalFuelledVolume",
      field: "consumptionData.totalFuelledVolume",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Vehicles.ProductGroupDescription",
      field: "consumptionData.productGroupDescription",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Vehicles.CalculatedAverageList",
      field: "consumptionData.calculatedAverage",
      fieldType: FieldType.CHIP_NUMBER,
    },
    {
      header: "Vehicles.CalculatedDeviation",
      field: "consumptionData.calculatedDeviation",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Vehicles.CalculatedDeviationPercentage",
      field: "consumptionData.calculatedDeviationPercentage",
      fieldType: FieldType.NUMBER,
    },
    {
      header: "Vehicles.VehicleConsumptionDescription",
      field: "consumptionData.vehicleConsumptionDescription",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Vehicles.LastOdometer",
      field: "defaultOdometer.lastOdometer",
      fieldType: FieldType.NUMBER,
    },
    {
      header: "Vehicles.TotalWorkingHoursList",
      field: "consumptionData.totalWorkingHours",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Vehicles.FuelRecords",
      field: "consumptionData.fuelRecords",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Vehicles.UpdatedOn",
      field: "updatedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
    },
    {
      header: "Vehicles.CreatedOn",
      field: "createdOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
    },
  ];

  constructor(
    public store: Store<AppState>,
    public translate: TranslateService
  ) {
    this.isLoading$ = this.store.pipe(select(getLoadingVehiclesState));
    this.vehicleList$ = this.store.pipe(select(loadedVehiclesListState));
    this.excelVehicleList$ = this.store.pipe(select(vehiclesStateExcelList));
    this.categories$ = this.store.pipe(select(loadedCategoriesListPartyState));
    this.properties$ = this.store.pipe(select(loadedPropertiesListPartyState));
    this.totalVehicles$ = this.store.pipe(select(vehiclesStateTotalVehicles));
  }

  setTableCustomElements() {
    this.loadedVehicletSub = this.vehicleList$
      .pipe(skip(1))
      .subscribe((vehicles: any[]) => {
        this.vehicleList = vehicles;
        if (
          this.vehicleList !== undefined &&
          this.vehicleList !== null &&
          this.vehicleList?.length > 0
        ) {
          this.vehicleList = this.vehicleList.map((vehicle) => {
            let treatedVehicle = vehicle;
            treatedVehicle = this.setCustomStatusChip(treatedVehicle);
            return treatedVehicle;
          });
        }
      });
  }

  setCustomStatusChip(vehicle: any) {
    let vehicleLicensePlateData = null;
    const defaultVehicle = {
      ...vehicle,
    };
    const { statusCode, statusDescription } = vehicle;
    if (statusDescription === undefined) {
      return defaultVehicle;
    }

    if (statusDescription !== undefined && statusDescription !== null) {
      let style: CHIP_STYLE = CHIP_STYLE.INACTIVE;
      if (statusCode !== undefined && statusCode !== null) {
        switch (statusCode) {
          case "ACTIVE":
            style = CHIP_STYLE.SUCCESS;
            break;
          case "INACTIVE":
            style = CHIP_STYLE.INACTIVE;
            break;
          case "BLOCKED":
            style = CHIP_STYLE.DANGER;
            break;
        }
      }

      if (defaultVehicle) {
        vehicleLicensePlateData = {
          value: defaultVehicle.licensePlate,
          icon: "directions_car",
        };
      }
      let styleSettings = getChipStyle(style);

      var calculatedAverageData = null;

      if (vehicle?.consumptionData?.calculatedAverage) {
        calculatedAverageData = {
          value: vehicle?.consumptionData?.calculatedAverage,
          icon: null,
          style: null,
          tooltip: null,
        };

        if (
          vehicle?.consumptionData?.calculatedDeviation >
          vehicle?.consumptionData?.targetDeviation
        ) {
          calculatedAverageData.icon = "priority_high";
          calculatedAverageData.style = "background-color: #F00; color: #FFF;";
          calculatedAverageData.tooltip = this.translate.instant(
            "Vehicles.averageAbove"
          );
        } else if (vehicle?.consumptionData?.calculatedDeviation < 0) {
          let successStyle = getChipStyle(CHIP_STYLE.SUCCESS);
          calculatedAverageData.icon = "check";
          calculatedAverageData.style = successStyle.style;
          calculatedAverageData.tooltip = this.translate.instant(
            "Vehicles.averageBelow"
          );
        } else {
          let successStyle = getChipStyle(CHIP_STYLE.INACTIVE);
          calculatedAverageData.style = successStyle.style;
          calculatedAverageData.tooltip = this.translate.instant(
            "Vehicles.averageExpected"
          );
        }
      }

      return {
        ...vehicle,
        statusDescription: {
          value: statusDescription,
          icon: styleSettings.icon,
          style: styleSettings.style,
        },
        consumptionData: {
          ...vehicle.consumptionData,
          calculatedAverage: calculatedAverageData,
        },

        licensePlate: vehicleLicensePlateData,
      };
    }
  }

  getVehicleCategoriesProperties(usageCode: string, getVehicles: Function) {
    this.multiSubs = zip(
      this.categories$.pipe(skip(1)),
      this.properties$.pipe(skip(1))
    ).subscribe(([categories, properties]) => {
      if (categories && categories?.length) {
        const tableConfigCategories = setCategories(categories);
        if (tableConfigCategories) {
          this.tableConfig = this.tableConfig.concat(tableConfigCategories);
        }
      }

      if (properties && properties?.length) {
        const tableConfigProperties = setProperties(properties);
        if (tableConfigProperties) {
          this.tableConfig = this.tableConfig.concat(tableConfigProperties);
        }
      }

      this.getTableConfig(usageCode);
      getVehicles();
      this.setTableCustomElements();
    });

    this.store.dispatch(
      getCategoriesForListByUsage({
        pageReq: null,
        usageCode: USAGE_CODE,
      })
    );
    this.store.dispatch(
      getPropertiesForListByUsage({
        pageReq: null,
        usageCode: USAGE_CODE,
      })
    );
  }

  getTableConfig(usageCode: string) {
    this.store.dispatch(
      getVehicleTableConfig({
        options: {
          code: usageCode,
          settingType: ConfigSettingType.TABLE_CONFIG,
        },
      })
    );
  }

  getTableData(tableEvent: TableEvent, getVehicles: Function) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    getVehicles(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  getExportData(exportEvent: ExportEvent, getVehicleExcelList: Function) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = exportEvent;
    getVehicleExcelList(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  saveTableConfig(tableColumnInfo: ColumnSorterInfo[], usageCode: string) {
    this.store.dispatch(
      saveVehicleTableConfig({
        config: {
          code: usageCode,
          settingTypeCode: ConfigSettingType.TABLE_CONFIG,
          scope: ConfigScope.USER,
          value: { tableColumnInfo },
        },
      })
    );
  }
}
